/**
 * API Dashboard Service Implementation
 * 
 * This service provides the API implementation for dashboard operations,
 * communicating with the backend dashboard endpoints to manage dashboards,
 * widgets, and visualizations.
 * 
 * @module ApiDashboardService
 * @implements {IDashboardService}
 * 
 * Service Description:
 * The ApiDashboardService handles all dashboard-related API calls including:
 * - Dashboard CRUD operations
 * - Widget management and positioning
 * - Dashboard templates and sharing
 * - Real-time data fetching for visualizations
 * - Export functionality
 * 
 * @requires axios for HTTP requests
 * @requires IDashboardService interface
 */

import axios, { AxiosInstance } from 'axios';
import { Dashboard, DashboardWidget, WidgetType } from '../../../types/dashboard';
import { IDashboardService } from '../../interfaces/IDashboardService';
import { ServiceResponse, PaginatedResponse } from '../../interfaces/IDataSource';
import ApiClient from '../../../utils/apiClient';
import { enhancedTokenManager } from '../../EnhancedTokenManager';
import { DashboardError, DashboardErrorType } from '../../../types/errors';

export class ApiDashboardService implements IDashboardService {
  private api: AxiosInstance;
  private baseURL: string = '/api/v1/analytics/dashboards/';

  constructor() {
    this.api = ApiClient.getInstance();
    // Set timeout if not already configured
    this.api.defaults.timeout = 30000; // 30 seconds
  }

  /**
   * Verify authentication token is present
   * @private
   */
  private async verifyAuth(): Promise<boolean> {
    try {
      const token = await enhancedTokenManager.getToken();
      console.log('[ApiDashboardService] Auth token present:', !!token);
      if (!token) {
        console.error('[ApiDashboardService] No auth token available');
        return false;
      }
      return true;
    } catch (error) {
      console.error('[ApiDashboardService] Auth verification failed:', error);
      return false;
    }
  }

  /**
   * Handle API errors consistently
   * @private
   */
  private handleApiError(error: any, operation: string): never {
    console.error(`[ApiDashboardService] Error during ${operation}:`, error);
    
    // Network error (no response)
    if (!error.response) {
      throw new DashboardError(
        DashboardErrorType.NETWORK_ERROR,
        'Unable to connect to server. Please check your connection.',
        error
      );
    }
    
    const { status, data } = error.response;
    
    switch (status) {
      case 400:
        throw new DashboardError(
          DashboardErrorType.VALIDATION_ERROR,
          data.message || data.detail || 'Invalid dashboard data provided.',
          data,
          status
        );
      
      case 401:
        throw new DashboardError(
          DashboardErrorType.AUTH_ERROR,
          'Authentication required. Please log in again.',
          data,
          status
        );
      
      case 403:
        throw new DashboardError(
          DashboardErrorType.PERMISSION_ERROR,
          'You do not have permission to perform this action.',
          data,
          status
        );
      
      case 404:
        throw new DashboardError(
          DashboardErrorType.NOT_FOUND,
          'Dashboard not found.',
          data,
          status
        );
      
      case 500:
      case 502:
      case 503:
        throw new DashboardError(
          DashboardErrorType.SERVER_ERROR,
          'Server error. Please try again later.',
          data,
          status
        );
      
      default:
        throw new DashboardError(
          DashboardErrorType.UNKNOWN_ERROR,
          data.message || data.detail || 'An unexpected error occurred.',
          data,
          status
        );
    }
  }

  /**
   * Get all dashboards for the current user
   * 
   * @returns Promise resolving to paginated dashboard list
   * 
   * @example
   * ```typescript
   * const service = new ApiDashboardService();
   * const response = await service.getDashboards();
   * console.log(response.results); // Array of Dashboard objects
   * console.log(response.total); // Total count
   * ```
   * 
   * Error Handling:
   * - 401: User not authenticated
   * - 403: User lacks permission to view dashboards
   * - 500: Server error
   */
  async getDashboards(): Promise<PaginatedResponse<Dashboard>> {
    try {
      await this.verifyAuth();
      console.log('[ApiDashboardService] Fetching dashboards...');
      
      const response = await this.api.get<PaginatedResponse<Dashboard>>(this.baseURL);
      
      // Validate response structure
      if (!response.data || typeof response.data !== 'object') {
        throw new Error('Invalid response format');
      }
      
      return {
        results: response.data.results || [],
        count: response.data.count || 0,
        next: response.data.next || null,
        previous: response.data.previous || null
      };
    } catch (error) {
      this.handleApiError(error, 'fetching dashboards');
    }
  }

  /**
   * Get a specific dashboard by ID
   * 
   * @param id - Dashboard UUID
   * @returns Promise resolving to the Dashboard object
   * 
   * @example
   * ```typescript
   * const service = new ApiDashboardService();
   * try {
   *   const dashboard = await service.getDashboard('123e4567-e89b-12d3-a456-426614174000');
   *   console.log(dashboard.name);
   * } catch (error) {
   *   console.error('Dashboard not found');
   * }
   * ```
   * 
   * Error Handling:
   * - 404: Dashboard not found
   * - 403: User lacks permission to view this dashboard
   */
  async getDashboard(id: string): Promise<Dashboard> {
    try {
      const response = await this.api.get<Dashboard>(`${this.baseURL}${id}/`);
      return response.data;
    } catch (error) {
      this.handleApiError(error, `fetching dashboard ${id}`);
    }
  }

  /**
   * Create a new dashboard
   * 
   * @param dashboard - Partial dashboard object with required fields
   * @returns Promise resolving to the created Dashboard
   * 
   * @example
   * ```typescript
   * const service = new ApiDashboardService();
   * const newDashboard = await service.createDashboard({
   *   name: 'Executive Dashboard',
   *   type: 'executive',
   *   layout: { cols: 12, rowHeight: 100 }
   * });
   * ```
   * 
   * Error Handling:
   * - 400: Invalid dashboard data
   * - 403: User lacks permission to create dashboards
   */
  async createDashboard(dashboard: Partial<Dashboard>): Promise<Dashboard> {
    try {
      const response = await this.api.post<Dashboard>(this.baseURL, dashboard);
      return response.data;
    } catch (error) {
      this.handleApiError(error, 'creating dashboard');
    }
  }

  /**
   * Update an existing dashboard
   * 
   * @param id - Dashboard UUID
   * @param dashboard - Partial dashboard object with fields to update
   * @returns Promise resolving to the updated Dashboard
   * 
   * @example
   * ```typescript
   * const service = new ApiDashboardService();
   * const updated = await service.updateDashboard('123...', {
   *   name: 'Updated Dashboard Name',
   *   layout: { cols: 16, rowHeight: 120 }
   * });
   * ```
   */
  async updateDashboard(id: string, dashboard: Partial<Dashboard>): Promise<Dashboard> {
    try {
      const response = await this.api.patch<Dashboard>(`${this.baseURL}${id}/`, dashboard);
      return response.data;
    } catch (error) {
      this.handleApiError(error, `updating dashboard ${id}`);
    }
  }

  /**
   * Delete a dashboard
   * 
   * @param id - Dashboard UUID
   * @returns Promise resolving when deletion is complete
   * 
   * Error Handling:
   * - 404: Dashboard not found
   * - 403: User lacks permission to delete this dashboard
   * - 409: Dashboard cannot be deleted (e.g., it's the default)
   */
  async deleteDashboard(id: string): Promise<void> {
    try {
      await this.api.delete(`${this.baseURL}${id}/`);
    } catch (error) {
      this.handleApiError(error, `deleting dashboard ${id}`);
    }
  }

  /**
   * Duplicate an existing dashboard
   * 
   * @param id - Source dashboard UUID
   * @param name - Name for the duplicated dashboard
   * @returns Promise resolving to the new Dashboard
   */
  async duplicateDashboard(id: string, name: string): Promise<Dashboard> {
    try {
      const response = await this.api.post<Dashboard>(`${this.baseURL}${id}/duplicate`, { name });
      return response.data;
    } catch (error) {
      console.error(`Error duplicating dashboard ${id}:`, error);
      throw error;
    }
  }

  // Widget Operations

  /**
   * Get all widgets for a dashboard
   * 
   * @param dashboardId - Dashboard UUID
   * @returns Promise resolving to array of DashboardWidget objects
   * 
   * @example
   * ```typescript
   * const widgets = await service.getWidgets('dashboard-123');
   * widgets.forEach(widget => {
   *   console.log(`Widget: ${widget.name} at position (${widget.x}, ${widget.y})`);
   * });
   * ```
   */
  async getWidgets(dashboardId: string): Promise<DashboardWidget[]> {
    try {
      const response = await this.api.get<DashboardWidget[]>(`${this.baseURL}${dashboardId}/widgets`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching widgets for dashboard ${dashboardId}:`, error);
      throw error;
    }
  }

  /**
   * Get a specific widget by ID
   * 
   * @param id - Widget UUID
   * @returns Promise resolving to the DashboardWidget
   */
  async getWidget(id: string): Promise<DashboardWidget> {
    try {
      const response = await this.api.get<DashboardWidget>(`${this.baseURL}/widgets/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching widget ${id}:`, error);
      throw error;
    }
  }

  /**
   * Create a new widget on a dashboard
   * 
   * @param dashboardId - Dashboard UUID
   * @param widget - Partial widget object
   * @returns Promise resolving to the created DashboardWidget
   * 
   * @example
   * ```typescript
   * const newWidget = await service.createWidget('dashboard-123', {
   *   type: 'metric',
   *   name: 'Attendance Metric',
   *   position: { x: 0, y: 0, w: 4, h: 2 },
   *   config: { metricId: 'attendance' }
   * });
   * ```
   */
  async createWidget(dashboardId: string, widget: Partial<DashboardWidget>): Promise<DashboardWidget> {
    try {
      const response = await this.api.post<DashboardWidget>(
        `${this.baseURL}${dashboardId}/widgets`,
        widget
      );
      return response.data;
    } catch (error) {
      console.error('Error creating widget:', error);
      throw error;
    }
  }

  /**
   * Update an existing widget
   * 
   * @param id - Widget UUID
   * @param widget - Partial widget object with fields to update
   * @returns Promise resolving to the updated DashboardWidget
   */
  async updateWidget(id: string, widget: Partial<DashboardWidget>): Promise<DashboardWidget> {
    try {
      const response = await this.api.patch<DashboardWidget>(
        `${this.baseURL}/widgets/${id}`,
        widget
      );
      return response.data;
    } catch (error) {
      console.error(`Error updating widget ${id}:`, error);
      throw error;
    }
  }

  /**
   * Delete a widget from a dashboard
   * 
   * @param id - Widget UUID
   * @returns Promise resolving when deletion is complete
   */
  async deleteWidget(id: string): Promise<void> {
    try {
      await this.api.delete(`${this.baseURL}/widgets/${id}`);
    } catch (error) {
      console.error(`Error deleting widget ${id}:`, error);
      throw error;
    }
  }

  /**
   * Move a widget to a new position
   * 
   * @param id - Widget UUID
   * @param position - New x,y coordinates
   * @returns Promise resolving to the updated DashboardWidget
   */
  async moveWidget(id: string, position: { x: number; y: number }): Promise<DashboardWidget> {
    try {
      const response = await this.api.patch<DashboardWidget>(
        `${this.baseURL}/widgets/${id}/position`,
        position
      );
      return response.data;
    } catch (error) {
      console.error(`Error moving widget ${id}:`, error);
      throw error;
    }
  }

  /**
   * Resize a widget
   * 
   * @param id - Widget UUID
   * @param size - New width and height
   * @returns Promise resolving to the updated DashboardWidget
   */
  async resizeWidget(id: string, size: { width: number; height: number }): Promise<DashboardWidget> {
    try {
      const response = await this.api.patch<DashboardWidget>(
        `${this.baseURL}/widgets/${id}/size`,
        size
      );
      return response.data;
    } catch (error) {
      console.error(`Error resizing widget ${id}:`, error);
      throw error;
    }
  }

  // Widget Types

  /**
   * Get all available widget types
   * 
   * @returns Promise resolving to array of WidgetType objects
   * 
   * @example
   * ```typescript
   * const types = await service.getAvailableWidgetTypes();
   * types.forEach(type => {
   *   console.log(`${type.name}: ${type.description}`);
   * });
   * ```
   */
  async getAvailableWidgetTypes(): Promise<WidgetType[]> {
    try {
      const response = await this.api.get<WidgetType[]>(`${this.baseURL}/widget-types`);
      return response.data;
    } catch (error) {
      console.error('Error fetching widget types:', error);
      throw error;
    }
  }

  /**
   * Get default configuration for a widget type
   * 
   * @param type - Widget type identifier
   * @returns Promise resolving to default widget configuration
   */
  async getWidgetDefaults(type: string): Promise<Partial<DashboardWidget>> {
    try {
      const response = await this.api.get<Partial<DashboardWidget>>(
        `${this.baseURL}/widget-types/${type}/defaults`
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching widget defaults for type ${type}:`, error);
      throw error;
    }
  }

  // Dashboard Templates

  /**
   * Get available dashboard templates
   * 
   * @returns Promise resolving to array of template Dashboard objects
   */
  async getTemplates(): Promise<Dashboard[]> {
    try {
      const response = await this.api.get<Dashboard[]>(`${this.baseURL}/templates`);
      return response.data;
    } catch (error) {
      console.error('Error fetching dashboard templates:', error);
      throw error;
    }
  }

  /**
   * Create a new dashboard from a template
   * 
   * @param templateId - Template dashboard UUID
   * @param name - Name for the new dashboard
   * @returns Promise resolving to the created Dashboard
   */
  async createFromTemplate(templateId: string, name: string): Promise<Dashboard> {
    try {
      const response = await this.api.post<Dashboard>(
        `${this.baseURL}/templates/${templateId}/create`,
        { name }
      );
      return response.data;
    } catch (error) {
      console.error(`Error creating dashboard from template ${templateId}:`, error);
      throw error;
    }
  }

  // Sharing and Permissions

  /**
   * Share a dashboard with other users
   * 
   * @param id - Dashboard UUID
   * @param users - Array of user IDs to share with
   * @returns Promise resolving when sharing is complete
   */
  async shareDashboard(id: string, users: string[]): Promise<void> {
    try {
      await this.api.post(`${this.baseURL}${id}/share`, { users });
    } catch (error) {
      console.error(`Error sharing dashboard ${id}:`, error);
      throw error;
    }
  }

  /**
   * Set dashboard permissions
   * 
   * @param id - Dashboard UUID
   * @param permissions - Permission configuration object
   * @returns Promise resolving when permissions are updated
   */
  async setDashboardPermissions(id: string, permissions: any): Promise<void> {
    try {
      await this.api.put(`${this.baseURL}${id}/permissions`, permissions);
    } catch (error) {
      console.error(`Error setting dashboard permissions for ${id}:`, error);
      throw error;
    }
  }

  // Data Operations

  /**
   * Get chart data for a visualization
   * 
   * @param visualizationId - Visualization UUID
   * @param filters - Optional array of filter objects
   * @returns Promise resolving to chart data
   * 
   * @example
   * ```typescript
   * const chartData = await service.getChartData('viz-123', [
   *   { field: 'date', operator: 'gte', value: '2025-01-01' }
   * ]);
   * ```
   */
  async getChartData(visualizationId: string, filters?: any[]): Promise<any> {
    try {
      const response = await this.api.post(
        `${this.baseURL}/visualizations/${visualizationId}/data`,
        { filters }
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching chart data for ${visualizationId}:`, error);
      throw error;
    }
  }

  /**
   * Get metric data for a visualization
   * 
   * @param visualizationId - Visualization UUID
   * @param filters - Optional array of filter objects
   * @returns Promise resolving to metric data
   */
  async getMetricData(visualizationId: string, filters?: any[]): Promise<any> {
    try {
      const response = await this.api.post(
        `${this.baseURL}/visualizations/${visualizationId}/metric-data`,
        { filters }
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching metric data for ${visualizationId}:`, error);
      throw error;
    }
  }

  /**
   * Refresh all data for a dashboard
   * 
   * @param dashboardId - Dashboard UUID
   * @returns Promise resolving when refresh is complete
   */
  async refreshData(dashboardId: string): Promise<void> {
    try {
      await this.api.post(`${this.baseURL}${dashboardId}/refresh`);
    } catch (error) {
      console.error(`Error refreshing dashboard ${dashboardId}:`, error);
      throw error;
    }
  }

  // Export Operations

  /**
   * Export a dashboard in the specified format
   * 
   * @param id - Dashboard UUID
   * @param format - Export format ('pdf', 'png', or 'csv')
   * @returns Promise resolving to the exported file as Blob
   * 
   * @example
   * ```typescript
   * const blob = await service.exportDashboard('dashboard-123', 'pdf');
   * const url = URL.createObjectURL(blob);
   * window.open(url);
   * ```
   * 
   * Error Handling:
   * - 400: Invalid export format
   * - 500: Export generation failed
   */
  async exportDashboard(id: string, format: 'pdf' | 'png' | 'csv'): Promise<Blob> {
    try {
      const response = await this.api.get(
        `${this.baseURL}${id}/export`,
        {
          params: { format },
          responseType: 'blob',
        }
      );
      return response.data;
    } catch (error) {
      console.error(`Error exporting dashboard ${id}:`, error);
      throw error;
    }
  }
}

export default ApiDashboardService;